import { z } from 'zod';
/**
 * The health contract, shared by the API, the admin app and the worker app.
 *
 * This exists in Task 1 to prove the shared-package wiring end to end: one
 * schema, one inferred type, three consumers. Domain contracts arrive with
 * the domains that own them (Task 2 onward) — not here.
 */
export const healthStatusSchema = z.enum(['ok', 'degraded', 'error']);
export const healthCheckSchema = z.object({
    name: z.string(),
    status: healthStatusSchema,
    /** Round-trip duration of the check in milliseconds, when measured. */
    durationMs: z.number().nonnegative().optional(),
    /**
     * Operator-facing detail for the health RESPONSE body only. Never carries
     * PHI, credentials or location data — it is readable by any authenticated
     * operator. It is not logged: the application logging boundary
     * (safeLog/sanitizeForLog) does not allowlist `detail`; readiness logging
     * records `checkKey`, `ok` and `durationMs` instead.
     */
    detail: z.string().optional(),
    /**
     * Structured facts a caller might need to ACT on without parsing `detail`'s
     * prose (PH-JOB-004: a monitor scanning free text for queue names is not a
     * contract). Currently used for durable-jobs queue names — compile-time
     * constants declared in jobs/config.ts, never operator- or tenant-supplied
     * data — whose PERSISTED pg-boss policy no longer matches the code's
     * declaration. See apps/api/src/routes/health.ts's jobs-policy check.
     */
    queueDrift: z.array(z.string()).optional(),
});
export const healthResponseSchema = z.object({
    status: healthStatusSchema,
    service: z.string(),
    version: z.string(),
    /** Server clock at response time, ISO 8601 with offset. */
    time: z.string().datetime({ offset: true }),
    uptimeSeconds: z.number().nonnegative(),
    checks: z.array(healthCheckSchema),
});
export const readinessResponseSchema = healthResponseSchema;
/**
 * `/health` (apps/api/src/routes/health.ts) deliberately runs ZERO checks — it
 * exists to answer exactly one question, "did this process accept the
 * connection and answer", and answering AT ALL is the entire proof. It never
 * reuses {@link healthStatusSchema}'s 'ok' for that, because 'ok' there means
 * "checked, and found healthy" everywhere else this contract is used (`/live`,
 * `/ready`) — and a body that checks nothing has no verdict to report.
 * `'unchecked'` is a deliberately distinct literal so nothing that scans a
 * response for a health VERDICT can mistake this for one.
 */
export const bareLivenessStatusSchema = z.literal('unchecked');
export const bareLivenessResponseSchema = z.object({
    status: bareLivenessStatusSchema,
    service: z.string(),
    version: z.string(),
    time: z.string().datetime({ offset: true }),
    uptimeSeconds: z.number().nonnegative(),
    /** Always empty. See {@link bareLivenessStatusSchema}. */
    checks: z.tuple([]),
});
//# sourceMappingURL=health.js.map